import React, { useEffect, useState } from 'react';
import {
  fetchTrajectoryFiles,
  fetchTrajectoryDetails,
} from '../../services/api';
import { TrajectoryInfo } from '../../types/types';
import { knownTrajectoryColors, colorNames } from '../../utils/trajectoryColor';
import { extractDeploymentNumber } from '../../utils/deployment';

interface DeploymentTrajectoriesListProps {
  selectedDeployment: string;
  setSelectedInfo: React.Dispatch<React.SetStateAction<TrajectoryInfo | null>>;
}

const DeploymentTrajectoriesList: React.FC<DeploymentTrajectoriesListProps> = ({
  selectedDeployment,
  setSelectedInfo,
}) => {
  const [trajectoryFiles, setTrajectoryFiles] = useState<string[]>([]);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedDeployment) return;
    setTrajectoryFiles([]);
    fetchTrajectoryFiles(selectedDeployment)
      .then((data) => {
        const uniqueColors = new Set();
        const filteredTrajectories = data.trajectories.filter(
          (filePath: string) => {
            const fileName = filePath.split('/')[1];
            const trajectoryColor =
              Object.keys(knownTrajectoryColors).find((c) =>
                fileName.toLowerCase().startsWith(c)
              ) || 'red';
            if (!uniqueColors.has(trajectoryColor)) {
              uniqueColors.add(trajectoryColor);
              return true;
            }
            return false;
          }
        );
        setTrajectoryFiles(filteredTrajectories);
      })
      .catch((err) => {
        console.error('Error while retrieving trajectories:', err);
      });
  }, [selectedDeployment]);

  const handleTrajectoryClick = (fileName: string) => {
    const colorKey =
      Object.keys(knownTrajectoryColors).find((c) =>
        fileName.toLowerCase().startsWith(c)
      ) || 'red';
    const deploymentNumber = extractDeploymentNumber(selectedDeployment);

    if (deploymentNumber !== null) {
      fetchTrajectoryDetails(deploymentNumber, colorKey)
        .then((trajectory) => {
          if (trajectory?.color === selectedColor) {
            setSelectedInfo(null);
            setSelectedColor(null);
          } else {
            setSelectedInfo(trajectory);
            setSelectedColor(trajectory.color);
          }
        })
        .catch((err) =>
          console.error('Error while retrieving trajectory details:', err)
        );
    }
  };

  return (
    <div className="relative w-full max-w-screen-xl mx-auto py-8 px-4">
      <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-6">
        Displayed Trajectories
      </h2>
      <div className="space-y-4">
        <ul className="space-y-2">
          {trajectoryFiles.map((filePath: string) => {
            const [folder, fileName] = filePath.split('/');
            const trajectoryColor =
              Object.keys(knownTrajectoryColors).find((c) =>
                fileName.toLowerCase().startsWith(c)
              ) || 'red';
            const trajectoryLabel = `${colorNames[trajectoryColor as keyof typeof colorNames]} Trajectory`;
            return (
              <li
                key={`${folder}-${fileName}`}
                onClick={() => handleTrajectoryClick(fileName)}
                className="cursor-pointer p-3 rounded-lg transition-all duration-200 transform hover:scale-105 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium">{trajectoryLabel}</span>
                  <div
                    className="w-6 h-6 rounded-full"
                    style={{
                      backgroundColor:
                        knownTrajectoryColors[
                          trajectoryColor as keyof typeof knownTrajectoryColors
                        ],
                    }}
                  ></div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default DeploymentTrajectoriesList;
